import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, Users, Trophy, Heart } from 'lucide-react';

function HomePage() {
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calculateCountdown = () => {
      const target = new Date("2026-09-12T00:00:00").getTime();
      const now = new Date().getTime();
      const difference = target - now;

      if (difference > 0) {
        setCountdown({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor(difference % (1000 * 60 * 60 * 24) / (1000 * 60 * 60)),
          minutes: Math.floor(difference % (1000 * 60 * 60) / (1000 * 60)),
          seconds: Math.floor(difference % (1000 * 60) / 1000)
        });
      }
    };

    calculateCountdown();
    const interval = setInterval(calculateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-white via-gray-50 to-red-50">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI0RDMTQzQyIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-20"></div>
        
        {/* Left Crest Image */}
        <div className="absolute left-4 md:left-12 top-1/2 -translate-y-1/2 w-32 md:w-48 lg:w-64 opacity-20 hidden sm:block">
          <img
            src="https://static.lumi.new/85/850bfbc472ab28e1c978fead6dc3aebf.webp"
            alt="MOBA 91 Crest"
            className="w-full h-auto" />

        </div>

        {/* Right Crest Image */}
        <div className="absolute right-4 md:right-12 top-1/2 -translate-y-1/2 w-32 md:w-48 lg:w-64 opacity-20 hidden sm:block">
          <img
            src="https://static.lumi.new/85/850bfbc472ab28e1c978fead6dc3aebf.webp"
            alt="MOBA 91 Crest"
            className="w-full h-auto" />

        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <div className="inline-block px-6 py-2 bg-[#DC143C]/10 border border-[#DC143C]/30 rounded-full font-semibold mb-6 text-8xl text-[#DC143C] bg-[#FFFFFF]">MOBA 91

            </div>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6 text-black">
            EST. 1986
          </h1>
          
          <h2 className="text-2xl md:text-3xl font-semibold mb-8 text-[#DC143C]">

          </h2>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-12 leading-relaxed">United in brotherhood, excellence, and service. Celebrating 40 years of a distinguished alumni making a difference in Ghana and beyond.

          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-16">
            <Link
              to="/members"
              className="px-8 py-4 bg-[#DC143C] hover:bg-red-600 text-white rounded-lg font-semibold transition-all transform hover:scale-105 shadow-lg shadow-[#DC143C]/20">
              Join Our Community
            </Link>
            <Link
              to="/about-us"
              className="px-8 py-4 bg-white hover:bg-gray-50 border-2 border-[#DC143C] text-[#DC143C] rounded-lg font-semibold transition-all">
              Learn More
            </Link>
          </div>

          {/* Countdown Timer */}
          <div className="bg-white border-2 border-[#DC143C]/30 rounded-2xl p-6 md:p-8 shadow-xl">
            <h3 className="text-xl md:text-2xl font-bold mb-6 text-[#DC143C]">40th Anniversary Countdown</h3>
            <div className="grid grid-cols-4 gap-2 sm:gap-3 md:gap-4 max-w-2xl mx-auto">
              <CountdownUnit value={countdown.days} label="Days" />
              <CountdownUnit value={countdown.hours} label="Hours" />
              <CountdownUnit value={countdown.minutes} label="Minutes" />
              <CountdownUnit value={countdown.seconds} label="Seconds" />
            </div>
            <p className="mt-6 text-gray-600 text-sm">September 12, 2026</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-black">Our Community</h2>
            <p className="text-gray-600 text-lg">Building connections, creating impact</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard
              icon={<Users />}
              title="Active Members"
              description="Connect with fellow MOBA91 alumni across the globe"
              link="/members" />
            <FeatureCard
              icon={<Trophy />}
              title="Our Projects"
              description="Making a lasting impact at Mfantsipim and beyond"
              link="/projects" />
            <FeatureCard
              icon={<Calendar />}
              title="Events & Gallery"
              description="Relive memorable moments from our gatherings"
              link="/gallery" />
            <FeatureCard
              icon={<Heart />}
              title="Support Us"
              description="Contribute to our development initiatives"
              link="/contact" />
          </div>
        </div>
      </section>
    </div>);

}

function CountdownUnit({ value, label }: {value: number;label: string;}) {
  return (
    <div className="bg-white border-2 border-[#DC143C]/20 rounded-xl p-3 sm:p-4 md:p-8 shadow-md">
      <div className="text-2xl sm:text-3xl md:text-5xl font-bold mb-1 sm:mb-2 text-[#DC143C]">
        {value.toString().padStart(2, "0")}
      </div>
      <div className="text-[10px] sm:text-xs md:text-sm text-gray-600 uppercase tracking-wide whitespace-nowrap">{label}</div>
    </div>);

}

function FeatureCard({ icon, title, description, link }: any) {
  return (
    <Link
      to={link}
      className="group bg-white border-2 border-[#DC143C]/20 rounded-xl p-6 hover:border-[#DC143C] hover:shadow-xl transition-all transform hover:scale-105">
      <div className="w-12 h-12 bg-[#DC143C]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#DC143C]/20 transition-all">
        <div className="text-[#DC143C]">{icon}</div>
      </div>
      <h3 className="text-xl font-bold mb-2 text-black group-hover:text-[#DC143C] transition-colors">{title}</h3>
      <p className="text-gray-600 text-sm">{description}</p>
    </Link>);

}

export default HomePage;